import { Stack, Typography } from '@mui/material'
import capitalize from 'lodash/capitalize'
import { useTranslation } from 'react-i18next'
import { SignalLevel } from '@omnea-app/omui/SignalLevel'
import { Request } from '@omnea-app/requests/requestsHttpClient'
import { requestPriorityEnumToNumberMap } from './utils'
import type { RequestPriorityEnum } from '@omnea-app/packages/universal/requests/requestPriority'

const getFormattedRequestPriorityString = (priority: Request['derivedFields']['priority']) => capitalize(priority)

const PRIORITY_COLOURS: Record<RequestPriorityEnum, string> = {
  LOWEST: 'purple.500',
  LOW: 'purple.500',
  MEDIUM: 'orange.500',
  HIGH: 'amber.900',
  HIGHEST: 'amber.900',
}

const REQUEST_PRIORITY_SCALE = [
  '',
  ...Object.entries(requestPriorityEnumToNumberMap)
    .sort(([, firstRating], [, secondRating]) => firstRating - secondRating)
    .map(([priority]) => capitalize(priority)),
]

type Props = {
  priority: Request['derivedFields']['priority']
  displayFormattedPriorityString?: boolean
  displayTooltip?: boolean
}

export const RequestPriority = ({ priority, displayFormattedPriorityString, displayTooltip }: Props) => {
  const { t } = useTranslation('requests')
  const formattedPriorityString = getFormattedRequestPriorityString(priority)

  return (
    <Stack direction="row" gap={2} sx={{ alignItems: 'center' }}>
      <SignalLevel
        rating={requestPriorityEnumToNumberMap[priority]}
        scale={REQUEST_PRIORITY_SCALE}
        filledColour={PRIORITY_COLOURS[priority]}
        size="md"
        tooltipTitle={displayTooltip ? t('priority.tooltip', { priority: formattedPriorityString }) : ''}
      />
      {displayFormattedPriorityString && (
        <Typography variant="body-regular" color="text.default">
          {formattedPriorityString}
        </Typography>
      )}
    </Stack>
  )
}
